import type { RiskLimits } from './limits';
import type { RiskParameter } from '@thinkalpha/locates-ws-client';

/** A firm-level override rule for one concrete symbol. */
export interface SymbolRiskRule extends RiskLimits {
    readonly id: string;
    readonly symbol: string;
}

/** An override rule for one provider; a `null` symbol applies to all symbols. */
export interface ProviderRiskRule extends RiskLimits {
    readonly id: string;
    readonly providerId: string;
    readonly symbol: string | null;
}

/** {@link ProviderRiskRule} with the provider's display name resolved for rendering. */
export interface ProviderRiskRuleRow extends ProviderRiskRule {
    readonly providerName: string;
}

/** An override rule for one account; a `null` symbol applies to all symbols. */
export interface AccountRiskRule extends RiskLimits {
    readonly id: string;
    readonly accountId: string;
    readonly symbol: string | null;
}

/** {@link AccountRiskRule} with the account's display name and code resolved for rendering. */
export interface AccountRiskRuleRow extends AccountRiskRule {
    readonly accountName: string;
    readonly accountCode: string;
}

/**
 * A read-only rule configured on the backend that can combine scopes in
 * ways the standard tabs don't support. A `null` scope column means the
 * rule doesn't constrain that dimension.
 */
export interface AdvancedRiskRule extends RiskLimits {
    readonly id: string;
    readonly firm: string;
    readonly memberFirm: string | null;
    readonly account: string | null;
    readonly route: string | null;
    readonly provider: string | null;
    readonly symbol: string | null;
    readonly enabled: boolean;
}

export type RiskParameterKind = 'symbol' | 'account' | 'provider' | 'advanced';

/**
 * Which tab a stored parameter belongs to. Mirrors the backend's
 * `riskFilterOf`: combining an account with a provider is "advanced",
 * a single narrower dimension names its tab, and a bare symbol is a
 * symbol rule. A parameter targeting nothing is refused on write, so
 * `null` only shows up defensively.
 */
export function classifyRiskParameter(parameter: RiskParameter): RiskParameterKind | null {
    if (parameter.accountId !== null && parameter.providerId !== null) return 'advanced';
    if (parameter.accountId !== null) return 'account';
    if (parameter.providerId !== null) return 'provider';
    return parameter.symbol !== null ? 'symbol' : null;
}

export function riskLimitsOfParameter(parameter: RiskParameter): RiskLimits {
    return { maxPrice: parameter.maxPrice, maxSize: parameter.maxSize, maxOrderTotal: parameter.maxOrderTotal };
}

export type AdvancedRuleFilter = 'all' | 'firm' | 'account' | 'provider';

/**
 * Apply the scope pill + free-text search to the advanced rules. A rule
 * matches a scope pill when that scope column is set on the rule.
 * "Firm" means the rule is not tied to a member firm, account, or
 * provider — per the design, symbol- or route-constrained rules still
 * count as firm-wide caps. The search matches any string column,
 * case-insensitively.
 */
export function filterAdvancedRules(
    rules: readonly AdvancedRiskRule[],
    filter: AdvancedRuleFilter,
    search: string,
): readonly AdvancedRiskRule[] {
    let filtered = rules;
    if (filter === 'firm') {
        filtered = filtered.filter(
            (rule) => rule.memberFirm === null && rule.account === null && rule.provider === null,
        );
    } else if (filter === 'account') {
        filtered = filtered.filter((rule) => rule.account !== null);
    } else if (filter === 'provider') {
        filtered = filtered.filter((rule) => rule.provider !== null);
    }
    const query = search.trim().toLowerCase();
    if (query === '') return filtered;
    return filtered.filter((rule) =>
        [rule.id, rule.firm, rule.memberFirm, rule.account, rule.route, rule.provider, rule.symbol].some(
            (value) => value !== null && value.toLowerCase().includes(query),
        ),
    );
}
